import type React from "react";
import { cn } from "tailwind-variants";
import type {
	ApiBlockGridItemModel,
	IApiElementModel,
	SettingsUmbBlockGridCompositionElementModel,
	SettingsUmbBlockGridCompositionPropertiesModel,
} from "#/_generated/umbraco/delivery-api/models";
import { BlockGridArea } from "#/_packages/umbrella/integrations/umbraco/property-components/block-grid-area.tsx";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import { ExternalVideo } from "#/_packages/umbrella/integrations/umbraco/utils/external-video-utils.ts";
import { getResponsiveImageProps } from "#/_packages/umbrella/integrations/umbraco/utils/image-utils.ts";
import {
	mapBlockLayoutCssFromBlockGrid,
	styleStringToObject,
} from "#/_packages/umbrella/utils/theme-utils.ts";
import { useCmsImageOptions } from "../../utils/media-composition";

type ISectionBlockProps = BlockItemProps & {
	model: ApiBlockGridItemModel;
};

export default function SectionUmbBlock({
	className,
	model,
}: ISectionBlockProps) {
	const layoutSettings = getLayoutSettings(model.settings);

	if (!layoutSettings?.theme)
		return (
			<BlockGridArea
				areas={model.areas}
				id={model.content.id}
				className={className}
			/>
		);

	const { theme, blockGridSettings } = layoutSettings;

	const layoutThemeCss = (
		theme?.backgroundColor?.replace("-muted", "") +
		" " +
		theme?.textColor
	).trim();
	const layoutStyles = (
		theme?.minHeight +
		" " +
		theme?.paddingTop +
		" " +
		theme?.paddingBottom +
		" " +
		theme?.backgroundColorOpacity
	).trim();
	const layoutContainerCss = (
		theme?.containerWidth +
		" " +
		theme?.containerAlignment
	).trim();

	return (
		<div
			className={cn(layoutThemeCss)}
			style={styleStringToObject(layoutStyles)}
		>
			<div className={layoutContainerCss}>
				{blockGridSettings.properties?.image?.[0]?.url ? (
					<SectionBlockImage
						id={model.content.id}
						properties={blockGridSettings.properties}
					/>
				) : null}
				{blockGridSettings.properties?.videoFromMediaLibrary?.[0]?.url ? (
					<SectionBlockVideo properties={blockGridSettings.properties} />
				) : null}
				{blockGridSettings.properties?.videoFromExternalLink ? (
					<SectionBlockExternalVideo
						properties={blockGridSettings.properties}
					/>
				) : null}
				<BlockGridArea
					className="_layout _block-grid [3/6]"
					areas={model.areas}
					id={model.content.id}
				/>
			</div>
		</div>
	);
}

function getLayoutSettings(settings: IApiElementModel | undefined) {
	const blockGridSettings =
		settings as SettingsUmbBlockGridCompositionElementModel;
	if (!blockGridSettings) return null;

	const theme = mapBlockLayoutCssFromBlockGrid(blockGridSettings);
	return { blockGridSettings, theme };
}

function SectionBlockImage({
	properties,
	id,
}: {
	properties: SettingsUmbBlockGridCompositionPropertiesModel;
	id: string;
}) {
	const mediaOptions = useCmsImageOptions(id);
	const style: React.CSSProperties | undefined =
		properties.backgroundMediaOpacity
			? { opacity: `calc(${properties.backgroundMediaOpacity} / 100);` }
			: undefined;

	const animationCss = properties.backgroundMediaAnimationType
		? "figure-img img-fit-cover"
		: `figure-img img-fit-cover ${properties.backgroundMediaAnimationType}`;

	const img = properties?.image?.[0] ? properties?.image[0] : undefined;
	const image = getResponsiveImageProps(img, { width: 1920, ...mediaOptions });
	return (
		<figure className="block-bg-img-wrapper figure" style={style}>
			<picture>
				<img className={animationCss} {...image} alt="" />
			</picture>
		</figure>
	);
}

function SectionBlockVideo({
	properties,
}: {
	properties: SettingsUmbBlockGridCompositionPropertiesModel;
}) {
	const video = properties?.videoFromMediaLibrary?.[0]
		? properties?.videoFromMediaLibrary[0]
		: undefined;

	if (!video) return;
	const style: React.CSSProperties | undefined =
		properties.backgroundMediaOpacity
			? { opacity: `calc(${properties.backgroundMediaOpacity} / 100);` }
			: undefined;
	return (
		<div
			className="layout-video absolute inset-0 overflow-hidden"
			style={style}
		>
			<video
				className={`w-full h-full object-fit-cover ${properties.backgroundMediaAnimationType}`.trim()}
				controls={
					properties.videoHideControls ? !properties.videoHideControls : true
				}
				autoPlay={properties.videoAutoplay ? properties.videoAutoplay : false}
				muted={properties.videoMuteAudio ? properties.videoMuteAudio : false}
				loop={
					properties.videoLoopPlayback ? properties.videoLoopPlayback : false
				}
				playsInline={
					properties.videoAutoplay ? properties.videoAutoplay : false
				}
				onContextMenu={
					!properties.videoEnableInteraction
						? (e) => e.preventDefault()
						: undefined
				}
			>
				<source src={`${video.url}`} type={getVideoType(video.url)} />
			</video>
		</div>
	);
}

function SectionBlockExternalVideo({
	properties,
}: {
	properties: SettingsUmbBlockGridCompositionPropertiesModel;
}) {
	if (!properties.videoFromExternalLink) return;
	const style: React.CSSProperties | undefined =
		properties.backgroundMediaOpacity
			? { opacity: `calc(${properties.backgroundMediaOpacity} / 100);` }
			: undefined;
	const video = ExternalVideo.create(
		properties.videoFromExternalLink,
		"",
		"",
		properties.videoAutoplay ?? false,
		properties.videoMuteAudio ?? false,
		properties.videoLoopPlayback ?? false,
		properties.videoHideControls ? properties.videoHideControls : false,
		properties.videoEnableInteraction
			? properties.videoEnableInteraction
			: false,
	);

	if (video.asIframe) {
		return (
			<div
				className={`layout-video overflow-hidden absolute inset-0 ${properties.videoHideControls ? "pointer-events-none" : ""}`}
				style={style}
			>
				<iframe
					title={`Video: ${video.title}`}
					className={`w-full h-full object-fit-cover`}
					src={video.url}
					width={640}
					height={360}
					frameBorder={0}
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
					referrerPolicy="strict-origin-when-cross-origin"
					allowFullScreen
					{...(video.enableInteraction
						? {}
						: { onContextMenu: (e) => e.preventDefault() })}
				/>
			</div>
		);
	}

	return (
		<div
			className="layout-video absolute inset-0 overflow-hidden"
			style={style}
		>
			<video
				className={`w-full h-full object-fit-cover ${properties.backgroundMediaAnimationType}`.trim()}
				controls={
					properties.videoHideControls ? !properties.videoHideControls : true
				}
				autoPlay={properties.videoAutoplay ? properties.videoAutoplay : false}
				muted={properties.videoMuteAudio ? properties.videoMuteAudio : false}
				loop={
					properties.videoLoopPlayback ? properties.videoLoopPlayback : false
				}
				playsInline={
					properties.videoAutoplay ? properties.videoAutoplay : false
				}
				onContextMenu={
					!properties.videoEnableInteraction
						? (e) => e.preventDefault()
						: undefined
				}
			>
				<source src={`${video.url}`} type={getVideoType(video.url)} />
			</video>
		</div>
	);
}

function getVideoType(url: string) {
	const extension = url.split("?")[0].split(".").pop()?.toLowerCase() ?? "";
	return `video/${extension}`;
}
